import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { workflowLogKeys } from "../workflow-log/workflow-log.hooks";
import { executionApi } from "./execution.api";
import { useExecutionStore } from "./execution.store";

export const executionKeys = {
	all: ["execution"] as const,
	status: (workflowId: string) => [...executionKeys.all, "status", workflowId] as const,
};

export const useGetExecutionStatus = (
	workflowId: string,
	options?: { polling?: boolean },
) => {
	const setStatus = useExecutionStore((state) => state.setStatus);

	const query = useQuery({
		queryKey: executionKeys.status(workflowId),
		queryFn: () => executionApi.getStatus(workflowId),
		enabled: Boolean(workflowId),
		refetchInterval: options?.polling ? 2000 : false,
	});

	useEffect(() => {
		if (query.data) setStatus(query.data.status);
	}, [query.data, setStatus]);

	return query;
};

export const useStartExecution = () => {
	const queryClient = useQueryClient();
	const setStatus = useExecutionStore((state) => state.setStatus);

	return useMutation({
		mutationFn: (workflowId: string) => executionApi.start(workflowId),
		onSuccess: (_data, workflowId) => {
			setStatus("running");
			queryClient.invalidateQueries({ queryKey: executionKeys.status(workflowId) });
			queryClient.invalidateQueries({ queryKey: workflowLogKeys.errorLogs(workflowId) });
		},
		onError: (_error, workflowId) => {
			queryClient.invalidateQueries({ queryKey: workflowLogKeys.errorLogs(workflowId) });
		},
	});
};

export const useStopExecution = () => {
	const queryClient = useQueryClient();
	const setStatus = useExecutionStore((state) => state.setStatus);

	return useMutation({
		mutationFn: (workflowId: string) => executionApi.stop(workflowId),
		onSuccess: (_data, workflowId) => {
			setStatus("stopped");
			queryClient.invalidateQueries({ queryKey: executionKeys.status(workflowId) });
			queryClient.invalidateQueries({ queryKey: workflowLogKeys.errorLogs(workflowId) });
		},
	});
};
